import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Key, 
  ChevronRight,
  AlertCircle,
  Lock,
  Unlock,
  Shield,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useSetup } from '../../../core/contexts/SetupContext';
import featureService from '../../../core/services/featureService';
import { getLicensingApiBase } from '../../utils/licensingApiConfig';
import { QAWAEM_LOGO_FULL } from '../../../products/saudi/config/qawaemAssets';
import { SAUDI_BRAND } from '../../../products/saudi/config/saudiBrandConfig';

/** Aligné avec `public/index.html` et `SetupWizard`. */
const QAWAEM_FAVICON_SRC = encodeURI(
  `${process.env.PUBLIC_URL ?? ''}/qawaem/favicon.png`
);

const ProductKey = ({ onLicenseValidated, variant = 'qawaem' }) => {
  const isQawaem = variant === 'qawaem';
  const { t } = useTranslation();
  const p = (key, opts) => t(`setup.productKey.${key}`, opts);
  const { nextStep } = useSetup();
  const [productKey, setProductKey] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // 🔑 LICENSING WORKER — localhost uses setupProxy (same Qawaem worker as production)
  const LICENSING_API_URL = getLicensingApiBase();

  // Monitor online status for kill switch functionality
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check if user already has a verified license on startup (Kill Switch Check)
  useEffect(() => {
    checkExistingLicense();
  }, []);

  // 🚨 KILL SWITCH: Check existing license on every app start
  const checkExistingLicense = async () => {
    const savedLicense = localStorage.getItem('qawaem_license');
    if (!savedLicense) return;

    try {
      const licenseData = JSON.parse(savedLicense);
      
      // Re-verify avec le worker si en ligne (kill switch).
      // Correction : verifyWithServer retourne un objet `{ success }`, pas un booléen.
      if (isOnline) {
        const verification = await verifyWithServer(licenseData.key);
        if (!verification.success) {
          localStorage.removeItem('qawaem_license');
          setError(p('errors.license_revoked'));
        }
      }
    } catch (error) {
      console.error('License check error:', error);
      localStorage.removeItem('qawaem_license');
    }
  };

  // Unused - commented out to fix ESLint warnings
  // const handleInputChange = (e) => {
  //   setProductKey(e.target.value.toUpperCase());
  //   if (error) setError('');
  // };

  // const formatProductKey = (key) => {
  //   // Format key with dashes every 5 characters (unlimited length)
  //   const cleaned = key.replace(/[^A-Z0-9]/g, '');
  //   let formatted = '';
  //   
  //   for (let i = 0; i < cleaned.length; i++) {
  //     if (i > 0 && i % 5 === 0) {
  //       formatted += '-';
  //     }
  //     formatted += cleaned[i];
  //   }
  //   
  //   return formatted;
  // };

  const handleKeyInput = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setProductKey(value); // Keep original formatting from paste/input
    if (error) setError('');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    const cleanPaste = paste.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setProductKey(cleanPaste); // Keep exact format from clipboard
    if (error) setError('');
  };

  // 🌐 API CALL: Verify product key with Cloudflare Worker
  const verifyWithServer = async (key) => {
    try {
      const url = `${LICENSING_API_URL}/verify?key=${encodeURIComponent(key)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      const contentType = response.headers.get('content-type') || '';
      const raw = await response.text();

      if (!contentType.includes('application/json')) {
        console.error('License verification: expected JSON, got', contentType, raw.slice(0, 120));
        const onLocalhost =
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        return {
          success: false,
          status: response.status,
          data: {
            error: onLocalhost
              ? p('errors.proxy_unavailable')
              : p('errors.server_connection'),
          },
        };
      }

      const data = JSON.parse(raw);

      return {
        success: response.status === 200 && data.success,
        status: response.status,
        data,
      };
    } catch (error) {
      console.error('License verification error:', error);
      return {
        success: false,
        status: 500,
        data: { error: p('errors.server_error') },
      };
    }
  };

  // 📝 VALIDATE PRODUCT KEY: Main validation function with real API
  const validateProductKey = async () => {
    const trimmed = productKey.trim();
    if (!trimmed) {
      setError(p('errors.enter_key'));
      return false;
    }

    if (!isOnline) {
      setError(p('errors.internet_required'));
      return false;
    }

    setIsValidating(true);
    setError('');

    try {
      const result = await verifyWithServer(trimmed);

      if (result.success) {
        // ✅ SUCCESS: Key is valid and activated
        const licenseData = {
          key: trimmed,
          keyType: result.data.keyType || 'FACTURATION', // Store license tier for feature gating
          appType: result.data.appType || 'FACTURATION', // Store app type for compatibility
          activatedAt: result.data.activatedAt || new Date().toISOString(),
          expiresAt: result.data.expiresAt || null, // Store TTL expiration (null = permanent)
          licenseMode: result.data.licenseMode || 'OFFLINE', // ONLINE or OFFLINE for sync
          companyId: result.data.companyId || null, // For ONLINE sync mode
          status: 'active',
          lastChecked: new Date().toISOString()
        };

        // Store license securely
        localStorage.setItem('qawaem_license', JSON.stringify(licenseData));
        
        // Clear feature service cache to reload with new license tier
        featureService.clearCache();
        
        console.log('✅ License activated successfully:', trimmed.substring(0, 8) + '...', '| Type:', licenseData.keyType, '| Mode:', licenseData.licenseMode);
        
        // Log expiration info
        if (licenseData.expiresAt) {
          const daysRemaining = Math.ceil((new Date(licenseData.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
          console.log(`⏰ License expires in ${daysRemaining} days`);
        } else {
          console.log('♾️ License is permanent (no expiration)');
        }
        
        return true;
        
      } else {
        // ❌ FAILED: Handle different error cases
        let errorMessage = p('errors.verification_failed');
        
        switch (result.data.code) {
          case 'BANNED_KEY':
            errorMessage = p('errors.key_banned');
            break;
          case 'KEY_NOT_FOUND':
            errorMessage = p('errors.key_not_found');
            break;
          case 'KEY_EXPIRED':
            errorMessage = p('errors.key_expired');
            break;
          default:
            errorMessage = result.data.error || p('errors.invalid_key');
        }
        
        setError(errorMessage);
        return false;
      }
      
    } catch (error) {
      console.error('Validation error:', error);
      setError(p('errors.connection_check'));
      return false;
      
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 🔑 REAL API VALIDATION: Connect to Cloudflare Worker Licensing System
    try {
      const isValid = await validateProductKey();
      
      if (isValid) {
        // License verified and stored - proceed to next step
        console.log('🎉 License verification successful - proceeding to account creation');
        if (onLicenseValidated) {
          onLicenseValidated(); // 🚀 UTILISER LE CALLBACK SÉCURISÉ
        } else {
          nextStep(); // Fallback si pas de callback
        }
      }
      // If validation fails, error message is already set in validateProductKey()
      
    } catch (error) {
      console.error('Submit error:', error);
      setError(p('errors.unexpected'));
      setIsValidating(false);
    }
  };
  
  // Unused - commented out to fix ESLint warnings
  // const handleSkip = () => {
  //   // For development purposes only - would be removed in production
  //   nextStep();
  // };

  /** Colonne centrée dans le viewport + carte glass Qawaem ou Qawaem. */
  if (isQawaem) {
    return (
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col justify-center overflow-x-hidden overflow-y-auto px-2 py-4 sm:px-3 sm:py-6 md:px-4">
        <div className="glass-modal relative mx-auto w-full max-w-xl overflow-hidden p-6 sm:max-w-2xl sm:p-8">
          <div className="relative flex flex-col">
            <div className="shrink-0 text-center">
              <motion.img
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                src={QAWAEM_LOGO_FULL}
                alt={SAUDI_BRAND.appName}
                className="mx-auto mb-3 h-12 w-auto object-contain sm:h-14"
                draggable={false}
              />
              <h1 className="text-xl font-bold text-console sm:text-2xl">مفتاح الترخيص</h1>
              <p className="mx-auto mt-2 max-w-lg text-sm leading-snug text-console-muted">
                أدخل مفتاح الترخيص الذي استلمته لتفعيل {SAUDI_BRAND.appName}.
              </p>
              <div
                className={`mt-3 inline-flex flex-wrap items-center justify-center gap-2 rounded-full border px-3 py-1 text-xs font-medium sm:text-sm ${
                  isOnline
                    ? 'border-emerald-300/60 bg-emerald-50 text-emerald-800'
                    : 'border-red-300/60 bg-red-50 text-red-700'
                }`}
              >
                {isOnline ? <Wifi className="h-4 w-4 shrink-0" aria-hidden /> : <WifiOff className="h-4 w-4 shrink-0" aria-hidden />}
                <span>{isOnline ? 'متصل' : 'غير متصل'}</span>
                {!isOnline && <span className="opacity-70">— يلزم الاتصال للتفعيل</span>}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-5 flex w-full min-w-0 flex-col gap-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-2 rounded-xl border border-red-300/50 bg-red-50 px-3 py-2.5 text-sm text-red-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{error}</span>
                </motion.div>
              )}

              <div className="w-full min-w-0">
                <label htmlFor="productKey" className="mb-1.5 block text-sm font-medium text-console-muted">
                  مفتاح الترخيص
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-[#8aab8a]" aria-hidden>
                    <Key className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                  <input
                    type="text"
                    id="productKey"
                    value={productKey}
                    onChange={handleKeyInput}
                    onPaste={handlePaste}
                    placeholder={p('placeholder')}
                    className="w-full min-w-0 glass-input py-2.5 ps-3 pe-10 font-mono text-sm tracking-wide sm:text-base"
                    maxLength={29}
                    autoComplete="off"
                    spellCheck={false}
                    dir="ltr"
                  />
                </div>
                <div className={`mt-2 flex items-center gap-2 text-xs sm:text-sm ${productKey ? 'font-medium text-[#5a855a]' : 'text-console-subtle'}`}>
                  {productKey ? <Unlock className="h-4 w-4 shrink-0" aria-hidden /> : <Lock className="h-4 w-4 shrink-0" aria-hidden />}
                  {productKey ? 'المفتاح جاهز للتحقق' : 'أدخل المفتاح للمتابعة'}
                </div>
              </div>

              <div className="w-full min-w-0 rounded-xl border border-[#c5d6c5]/50 bg-[#f4f7f4]/80 p-4">
                <div className="flex gap-3">
                  <Shield className="mt-0.5 h-5 w-5 shrink-0 text-[#6e9b6e]" aria-hidden />
                  <div className="min-w-0 text-right">
                    <h3 className="text-sm font-semibold text-console">لماذا مفتاح الترخيص؟</h3>
                    <p className="mt-1 text-sm leading-relaxed text-console-muted">
                      يفعّل مستوى الترخيص ويفتح الوحدات المصرّح بها وفق عقد الدعم.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-2 flex w-full justify-end border-t border-[#c5d6c5]/40 pt-4">
                <motion.button
                  type="submit"
                  disabled={isValidating}
                  whileHover={isValidating ? {} : { scale: 1.03 }}
                  whileTap={isValidating ? {} : { scale: 0.97 }}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold btn-primary-forest disabled:cursor-not-allowed disabled:opacity-55 sm:w-auto sm:text-base"
                >
                  {isValidating ? (
                    <>
                      <span className="h-5 w-5 shrink-0 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                      جاري التحقق…
                    </>
                  ) : (
                    <>
                      تفعيل
                      <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col justify-center overflow-x-hidden overflow-y-auto px-2 py-4 sm:px-3 sm:py-6 md:px-4">
      <div className="relative mx-auto w-full max-w-xl overflow-hidden rounded-none border-x-0 border-y border-white/15 bg-white/10 shadow-xl shadow-black/20 backdrop-blur-lg sm:max-w-2xl sm:rounded-3xl sm:border sm:border-white/20 sm:shadow-black/25">
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/15 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-col px-3 py-3 sm:px-5 sm:py-4 md:px-6 md:py-5 lg:px-8">
          <div className="shrink-0 text-center">
            <motion.img
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              src={QAWAEM_FAVICON_SRC}
              alt="Qawaem"
              className="mx-auto mb-2 h-9 w-9 object-contain drop-shadow-[0_8px_24px_rgb(15_23_42/0.55)] sm:mb-3 sm:h-11 sm:w-11 md:h-12 md:w-12 lg:h-14 lg:w-14"
              draggable={false}
            />
            <h1 className="text-xl font-bold text-white sm:text-2xl md:text-3xl">
              {p('title')}
            </h1>
            <p className="mx-auto mt-1 max-w-none px-1 text-xs leading-snug text-white/60 sm:mt-2 sm:text-sm md:text-base">
              {p('subtitle')}
            </p>
            <div
              className={`mt-3 inline-flex flex-wrap items-center justify-center gap-2 rounded-full border px-3 py-1 text-xs font-medium sm:mt-4 sm:px-4 sm:py-1.5 sm:text-sm ${
                isOnline
                  ? 'border-emerald-400/35 bg-emerald-500/10 text-emerald-200'
                  : 'border-red-400/40 bg-red-500/10 text-red-200'
              }`}
            >
              {isOnline ? <Wifi className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden /> : <WifiOff className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />}
              <span>{isOnline ? p('online') : p('offline')}</span>
              {!isOnline && <span className="text-white/50">{p('offline_hint')}</span>}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 flex w-full min-w-0 flex-col gap-4 sm:mt-5 sm:gap-5 md:gap-6">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 rounded-xl border border-red-500/35 bg-red-500/10 px-3 py-2.5 text-xs text-red-100 sm:px-4 sm:py-3 sm:text-sm"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" aria-hidden />
              <span>{error}</span>
            </motion.div>
          )}

          <div className="w-full min-w-0">
            <label htmlFor="productKey" className="mb-1.5 block text-xs font-medium text-white/70 sm:mb-2 sm:text-sm">
              {p('label')}
              <span className="ml-1 font-normal text-white/45 sm:ml-2">{p('label_format')}</span>
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35 sm:left-4" aria-hidden>
                <Key className="h-4 w-4 sm:h-5 sm:w-5" />
              </span>
              <input
                type="text"
                id="productKey"
                value={productKey}
                onChange={handleKeyInput}
                onPaste={handlePaste}
                placeholder={p('placeholder')}
                className="w-full min-w-0 rounded-lg border border-white/20 bg-white/5 py-2 pl-10 pr-3 font-mono text-sm tracking-wide text-white placeholder:text-white/30 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 sm:rounded-xl sm:py-2.5 sm:pl-11 sm:pr-4 sm:text-base md:pl-12 md:text-[0.9375rem]"
                maxLength={29}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div
              className={`mt-2 flex items-center gap-2 text-xs sm:mt-3 sm:text-sm ${
                productKey ? 'font-medium text-emerald-300' : 'text-white/50'
              }`}
            >
              {productKey ? <Unlock className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden /> : <Lock className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />}
              {productKey ? p('key_ready') : p('key_enter')}
            </div>
            <p className="mt-2 text-[11px] leading-snug text-white/35 sm:text-xs">
              {p('key_hint')}
            </p>
          </div>

          <div className="w-full min-w-0 rounded-xl border border-blue-400/25 bg-blue-500/10 p-3 backdrop-blur-sm sm:rounded-2xl sm:p-4 md:p-5">
            <div className="flex gap-2.5 sm:gap-3">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-blue-300 sm:mt-1 sm:h-5 sm:w-5" aria-hidden />
              <div className="min-w-0 text-left">
                <h3 className="text-sm font-semibold text-white sm:text-base">{p('why_title')}</h3>
                <p className="mt-1 text-xs leading-relaxed text-white/65 sm:text-sm md:text-base">
                  {p('why_body')}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex w-full justify-end border-t border-white/10 pt-4 sm:mt-5 sm:pt-5">
            <motion.button
              type="submit"
              disabled={isValidating}
              whileHover={isValidating ? {} : { scale: 1.03 }}
              whileTap={isValidating ? {} : { scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400 }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 disabled:cursor-not-allowed disabled:opacity-55 sm:w-auto sm:px-6 sm:py-3 md:text-base"
            >
              {isValidating ? (
                <>
                  <span className="h-5 w-5 shrink-0 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                  {p('validating')}
                </>
              ) : (
                <>
                  {p('activate')}
                  <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                </>
              )}
            </motion.button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default ProductKey;
